import React from "react";
import { FaCheckCircle } from "react-icons/fa";

export default function LocationEditorialSection({
  title,
  paragraphs = [],
  background = "bg-[#fffaf0]",
  reverse = false,
  image = "",
  imageAlt = "",
}) {
  if (!title || paragraphs.length === 0) return null;

  const isDark = reverse;

  return (
    <section
      className={`relative overflow-hidden py-14 sm:py-16 lg:py-24 ${
        isDark ? "bg-[#071426]" : background
      }`}
    >
      {/* Background */}
      {isDark ? (
        <>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#071426] via-[#0b2344] to-[#06101f]" />
          <div className="pointer-events-none absolute left-[-220px] top-[-180px] h-[520px] w-[520px] rounded-full bg-[#d7a51f]/14 blur-3xl" />
          <div className="pointer-events-none absolute right-[-260px] bottom-[-240px] h-[620px] w-[620px] rounded-full bg-[#2f73d8]/14 blur-3xl" />
        </>
      ) : (
        <>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.95),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(215,165,31,0.14),transparent_36%)]" />
          <div className="pointer-events-none absolute left-[-220px] top-[-180px] h-[520px] w-[520px] rounded-full bg-[#f2d36a]/18 blur-3xl" />
          <div className="pointer-events-none absolute right-[-260px] bottom-[-240px] h-[620px] w-[620px] rounded-full bg-[#0b3f7a]/8 blur-3xl" />
        </>
      )}

      <div className="relative mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-10">
        <div
          className={`grid items-center gap-12 lg:gap-16 ${
            reverse
              ? "lg:grid-cols-[1.05fr_0.95fr]"
              : "lg:grid-cols-[0.95fr_1.05fr]"
          }`}
        >
          {/* TITLE + IMAGE SIDE */}
          <div className={reverse ? "lg:order-2" : ""}>
            <div
              className={`mb-6 flex items-center gap-4 ${
                reverse ? "lg:justify-center" : ""
              }`}
            >
              <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#d7a51f]" />
              <span className="h-2.5 w-2.5 rotate-45 bg-[#d7a51f]" />
              <span className="h-px w-16 bg-gradient-to-r from-[#d7a51f] to-transparent" />
            </div>

            {/* Title made little smaller */}
            <h2
              className={`max-w-[620px] font-serif text-[28px] font-extrabold leading-tight tracking-[-0.03em] sm:text-[38px] lg:text-[48px] ${
                isDark ? "text-white" : "text-[#071426]"
              } ${reverse ? "mx-auto text-center" : ""}`}
            >
              {title}
            </h2>

            {/* Image below title */}
            {image && (
              <div
                className={`mt-8 overflow-hidden rounded-[28px] border shadow-[0_24px_70px_rgba(0,0,0,0.14)] ${
                  isDark
                    ? "border-[#d7a51f]/35 bg-white/[0.06]"
                    : "border-[#ead8a8] bg-white"
                } ${reverse ? "mx-auto max-w-[520px]" : "max-w-[520px]"}`}
              >
                <img
                  src={image}
                  alt={imageAlt || title}
                  className="h-[260px] w-full object-cover sm:h-[320px] lg:h-[360px]"
                />
              </div>
            )}

        
          </div>

          {/* CONTENT SIDE */}
          <div className={reverse ? "lg:order-1" : ""}>
            <div className="relative">
              <div
                className={`absolute top-0 hidden h-full w-px lg:block ${
                  reverse ? "right-[-32px]" : "left-[-32px]"
                } bg-gradient-to-b from-transparent via-[#d7a51f]/60 to-transparent`}
              />

              <div className="space-y-5">
                {paragraphs.map((paragraph, index) => (
                  <div
                    key={index}
                    className={`group relative overflow-hidden rounded-[26px] border p-6 shadow-[0_22px_65px_rgba(0,0,0,0.12)] transition duration-300 hover:-translate-y-1 sm:p-7 ${
                      isDark
                        ? "border-[#d7a51f]/25 bg-gradient-to-br from-[#123056] via-[#0d2545] to-[#081a31] hover:border-[#d7a51f]/60"
                        : "border-[#ead8a8] bg-white/85 hover:border-[#d7a51f]/70"
                    }`}
                  >
                    <div
                      className={`pointer-events-none absolute inset-0 ${
                        isDark
                          ? "bg-gradient-to-br from-white/[0.06] via-transparent to-transparent"
                          : "bg-gradient-to-br from-white via-transparent to-transparent"
                      }`}
                    />

                    <div className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-[#d7a51f]/12 blur-2xl" />

                    <div className="relative flex gap-5">
                      <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#d7a51f] via-[#b98210] to-[#6f4507] shadow-[0_12px_26px_rgba(185,130,16,0.32)]">
                        <FaCheckCircle className="text-[17px] text-white drop-shadow-[0_2px_5px_rgba(0,0,0,0.35)]" />
                      </div>

                      <p
                        className={`text-[16px] font-medium leading-8 sm:text-[18px] lg:leading-9 ${
                          isDark ? "text-white/90" : "text-[#334155]"
                        } ${index === paragraphs.length - 1 ? "font-bold" : ""}`}
                      >
                        {paragraph}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className={`mt-8 h-px w-full bg-gradient-to-r ${
                  isDark
                    ? "from-transparent via-[#d7a51f] to-transparent"
                    : "from-[#d7a51f]/70 via-[#ead8a8] to-transparent"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}